"use client";

import { useCallback, useEffect, useState } from "react";
import type { PushPreferences } from "../push-notifications";

export type PushEnableError =
  | "unsupported"
  | "denied"
  | "notConfigured"
  | "failed";

export interface PushDeviceSubscription {
  id: string;
  platform: "web" | "ios" | "android";
  endpoint: string;
}

export interface PushSettingsSnapshot {
  preferences: PushPreferences;
  subscriptions: PushDeviceSubscription[];
  vapidPublicKey: string | null;
}

export interface PushSettingsClient {
  load: () => Promise<PushSettingsSnapshot>;
  savePreferences: (preferences: PushPreferences) => Promise<void>;
  enable: () => Promise<
    { ok: true; endpoint: string } | { ok: false; error: PushEnableError }
  >;
  disable: (endpoint: string) => Promise<void>;
  currentEndpoint: () => Promise<string | null>;
}

export function usePushNotificationSettings(client: PushSettingsClient) {
  const [snapshot, setSnapshot] = useState<PushSettingsSnapshot | null>(null);
  const [currentEndpoint, setCurrentEndpoint] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(false);
  const [enableError, setEnableError] = useState<PushEnableError | null>(null);

  const reload = useCallback(async () => {
    try {
      const [next, endpoint] = await Promise.all([
        client.load(),
        client.currentEndpoint(),
      ]);
      setSnapshot(next);
      setCurrentEndpoint(endpoint);
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const savePreferences = useCallback(
    async (preferences: PushPreferences) => {
      setSaving(true);
      try {
        await client.savePreferences(preferences);
        setSnapshot((current) =>
          current ? { ...current, preferences } : current,
        );
      } finally {
        setSaving(false);
      }
    },
    [client],
  );

  const enable = useCallback(async (): Promise<PushEnableError | null> => {
    setPending(true);
    setEnableError(null);
    try {
      const result = await client.enable();
      if (!result.ok) {
        setEnableError(result.error);
        return result.error;
      }
      setCurrentEndpoint(result.endpoint);
      await reload();
      return null;
    } catch {
      setEnableError("failed");
      return "failed";
    } finally {
      setPending(false);
    }
  }, [client, reload]);

  const disable = useCallback(async () => {
    if (!currentEndpoint) {
      return;
    }
    setPending(true);
    setEnableError(null);
    try {
      await client.disable(currentEndpoint);
      setCurrentEndpoint(null);
      await reload();
    } catch {
      setEnableError("failed");
    } finally {
      setPending(false);
    }
  }, [client, currentEndpoint, reload]);

  return {
    snapshot,
    currentEndpoint,
    status,
    saving,
    pending,
    enableError,
    savePreferences,
    enable,
    disable,
    reload,
  };
}
